import { v4 } from 'uuid'

import { MEGAFON_PROMOCODE } from '@app/constants/Misc'

import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import type { PriceInputValue } from '@app/utils/PriceInputValue'

import { spawn } from '@app/packages/disposable/spawn'

import { promocodesSyncSlice } from '@app/store/slices/promocodes'
import { invalidateReferrals } from '@app/store/slices/referrals'
import { createThunk } from '@app/store/thunk'

import { assertMountPush, getMount } from '@app/components/Mount/actions'

import { callV2Api } from './api'
import { refreshUser } from './user'

export function createSubscription(period_months: 1 | 3 | 6 | 12 = 1) {
  return createThunk(async dispatch => {
    const { postSubscription } = await import('@app/packages/api/v2/actions/subscription')
    await dispatch(callV2Api(postSubscription(period_months)))

    await dispatch(refreshUser())
  })
}

export function cancelSubscription() {
  return createThunk(async dispatch => {
    const { putSubscriptionCancel } = await import('@app/packages/api/v2/actions/subscription')
    await dispatch(callV2Api(putSubscriptionCancel()))

    await dispatch(refreshUser())
  })
}

export function restoreSubscription() {
  return createThunk(async dispatch => {
    const { putSubscriptionRestore } = await import('@app/packages/api/v2/actions/subscription')
    await dispatch(callV2Api(putSubscriptionRestore()))

    await dispatch(refreshUser())
  })
}

export function purchaseInvoiceViaChargeModal({
  invoiceId,
  amount,
  description,
  canBind = true,
  providers,
}: {
  invoiceId: string
  amount: PriceInputValue
  description: string
  canBind?: boolean
  providers?: ('cloudpayments' | 'stripe')[]
}) {
  return createThunk<Promise<{ type: 'success' } | { type: 'cancel' }>>(async dispatch => {
    const { promise, resolve } = Promise.withResolvers<{ type: 'success'; bind: boolean } | { type: 'cancel' }>()
    let resolved = false
    await dispatch(
      assertMountPush(
        import('@app/components/ChargeModal/ChargeModal').then(m => m.ChargeModal),
        {
          props: {
            invoiceId,
            amount,
            description,
            canBind,
            providers,
            callback: async (data, close) => {
              if (resolved) return
              resolved = true
              await close()
              resolve(data)
            },
          },
        }
      )
    )

    return promise
  })
}

export function applyPromocode(code: string) {
  return createThunk(async dispatch => {
    const { postPromoUsages } = await import('@app/packages/api/v2/actions/promo_usages')
    const response = await dispatch(callV2Api(postPromoUsages(code)))

    // A code can change the subscription, the referral rewards and the list of used codes, whichever path applied it.
    dispatch(invalidateReferrals())
    dispatch(promocodesSyncSlice.set({ uuid: v4() }))
    // In the background: a profile that fails to reload must not make an applied code look refused.
    spawn(() => dispatch(refreshUser()), reportSpawnFailure('Profile refresh after a promocode'))

    if (code.toLocaleLowerCase() === MEGAFON_PROMOCODE) {
      dispatch(getMount())?.push(import('@app/components/MegafonModal/MegafonModal').then(m => m.MegafonModal))
    }

    return response
  })
}
